import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Flame, Plus, Trash2, Check, Circle } from "lucide-react";
import ReceiptUploader from "@/components/ReceiptUploader";
import ScanStrip from "@/components/ScanStrip";
import AssigneePicker, { AssigneeChip } from "@/components/AssigneePicker";
import { toast } from "sonner";

const CATEGORIES = ["Utility", "Subscription", "Rent/Mortgage", "Tax", "Other"];

export default function Bills() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("unpaid");
  const [open, setOpen] = useState(false);
  const blank = { name: "", category: "Utility", amount: "", due_date: "", recurrence: "monthly", paid: false, assignee_id: null };
  const [form, setForm] = useState(blank);
  const [scanConfidence, setScanConfidence] = useState(null);

  const load = useCallback(async () => {
    const [{ data }, mres] = await Promise.all([
      api.get("/bills"),
      api.get("/household/members").catch(() => ({ data: [] })),
    ]);
    setItems(data);
    setMembers(Array.isArray(mres.data) ? mres.data : (mres.data?.members || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items
    .filter((b) => (tab === "unpaid" ? !b.paid : b.paid))
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const total = filtered.reduce((s, b) => s + (Number(b.amount) || 0), 0);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/bills", { ...form, amount: form.amount ? parseFloat(form.amount) : null });
      setOpen(false); setForm(blank); toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (id) => { await api.post(`/bills/${id}/toggle`); load(); };
  const remove = async (id) => { await api.delete(`/bills/${id}`); load(); };

  // v27.18 — Bill scan: shared ScanStrip merges scanner UI + toast. We map the
  // response into the Bills form; the missing-field warning is nicer than
  // a generic success toast so we hand-craft it here.
  const applyScannedBill = (data) => {
    setForm({
      name: data.name || "",
      category: data.category || "Utility",
      amount: data.amount != null ? String(data.amount) : "",
      due_date: data.due_date || "",
      recurrence: data.recurrence || "monthly",
      paid: false,
    });
    const missing = [];
    if (!data.name) missing.push("name");
    if (data.amount == null) missing.push("amount");
    if (!data.due_date) missing.push("due date");
    if (missing.length) {
      // Toast is already fired by ScanStrip — this warning is an extra nudge.
      toast.warning(`Please add ${missing.join(" & ")}.`);
    }
  };

  const openAddDialog = () => {
    setForm(blank);
    setScanConfidence(null);
    setOpen(true);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Money flow"
        title="Bills & utilities"
        subtitle="Stay on top of every recurring payment."
        action={
          <Button data-testid="bill-add-button" onClick={openAddDialog}
            className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-6 h-11">
            <Plus size={16} className="mr-1.5" /> Add bill
          </Button>
        }
      />

      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div className="bg-hearth-paper border border-hearth-line rounded-full p-1 flex">
          {[["unpaid", "Unpaid"], ["paid", "Paid"]].map(([k, l]) => (
            <button key={k} data-testid={`bill-tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-2 text-sm rounded-full transition ${tab === k ? "bg-hearth-terracotta text-white" : "text-hearth-mute hover:text-hearth-ink"}`}>{l}</button>
          ))}
        </div>
        <div className="text-right">
          <p className="caption text-hearth-mute">{tab === "unpaid" ? "Outstanding" : "Settled"}</p>
          <p className="stat-num text-hearth-ink">{money(total)}</p>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Flame} title={tab === "unpaid" ? "All caught up." : "No paid bills yet."} message={tab === "unpaid" ? "Bills come in handy when they're tracked — add the next one above." : "Mark a bill paid to see it here."} />
      ) : (
        <div className="warm-card divide-y divide-hearth-line/70">
          {filtered.map((b) => (
            <div key={b.id} data-testid={`bill-row-${b.id}`} className="p-5">
              <div className="flex items-center gap-3">
                <button data-testid={`bill-toggle-${b.id}`} onClick={() => toggle(b.id)}
                        className="w-8 h-8 rounded-full border-2 border-hearth-line hover:border-hearth-clay grid place-items-center shrink-0 transition">
                  {b.paid ? <Check size={14} className="text-hearth-sage" /> : <Circle size={10} className="text-hearth-mute fill-current" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl text-hearth-ink truncate">{b.name}</div>
                  <div className="caption text-hearth-mute mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{b.category} · {b.recurrence}</span>
                    <AssigneeChip assigneeId={b.assignee_id} members={members} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-hearth-ink font-medium font-mono">{money(b.amount)}</div>
                  <div className="caption text-hearth-mute mt-0.5 font-mono">{b.due_date || "no date"}</div>
                </div>
                <button data-testid={`bill-delete-${b.id}`} onClick={() => remove(b.id)} className="p-2 rounded-full text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10 ml-2">
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-hearth-line/70">
                <ReceiptUploader kind="bills" itemId={b.id} receipts={b.receipts || []} onChange={load} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Add a bill</DialogTitle>
            <DialogDescription className="sr-only">Add or scan a new recurring bill or one-off invoice.</DialogDescription>
          </DialogHeader>

          {/* v27.18 — Scan invoice via Claude Vision. Uses shared ScanStrip.
              User always confirms + edits before Save. */}
          <ScanStrip
            testId="bill-scan"
            label="Snap a photo of the bill — we'll fill this out."
            buttonLabel="Scan invoice"
            endpoint="/bills/scan-invoice"
            confidence={scanConfidence}
            onConfidence={setScanConfidence}
            onScanned={applyScannedBill}
            successPrefix="Filled from your invoice"
          />

          <div className="space-y-4">
            <div>
              <Label className="caption text-hearth-mute">Name</Label>
              <Input data-testid="bill-form-name" className="input-warm mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Electricity" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption text-hearth-mute">Recurrence</Label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="bill-form-recurrence"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Once</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="fortnightly">Fortnightly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Amount</Label>
                <Input data-testid="bill-form-amount" type="number" step="0.01" className="input-warm mt-1.5 h-11" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div>
                <Label className="caption text-hearth-mute">Due date</Label>
                <Input data-testid="bill-form-due" type="date" className="input-warm mt-1.5 h-11" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <AssigneePicker
              value={form.assignee_id}
              onChange={(id) => setForm({ ...form, assignee_id: id })}
              label="Who's paying this?"
              testId="bill-form-assignee"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="bill-form-save" onClick={save} className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
